/**
 * isBlockedEmailProvider.js — frontend mirror of the backend
 * `email_validation.py` host gate.
 *
 * Why duplicate the list:
 *   We want real-time feedback on the registration / waitlist forms
 *   (escalate the static amber notice to a red error the moment the
 *   user finishes typing an @outlook / @hotmail / @live address)
 *   without round-tripping every keystroke to the server. The backend
 *   remains the only enforcement point; this just lets the UI be
 *   helpful before submit.
 *
 * Keep this list in lockstep with /app/backend/email_validation.py
 * (BLOCKED_EMAIL_HOSTS). If you add an entry to one, add it to both.
 */

export const BLOCKED_EMAIL_HOSTS = new Set([
  // Outlook
  'outlook.com',
  'outlook.co.uk',
  'outlook.fr',
  'outlook.de',
  'outlook.es',
  'outlook.it',
  'outlook.dk',
  'outlook.jp',
  'outlook.in',
  'outlook.ie',
  'outlook.pt',
  // Hotmail
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.co.jp',
  'hotmail.fr',
  'hotmail.de',
  'hotmail.es',
  'hotmail.it',
  'hotmail.com.br',
  'hotmail.com.ar',
  'hotmail.com.mx',
  // Live
  'live.com',
  'live.co.uk',
  'live.com.au',
  'live.fr',
  'live.de',
  'live.it',
  'live.nl',
  'live.ca',
  // Other Microsoft consumer
  'msn.com',
  'windowslive.com',
  'passport.com',
]);

/**
 * Return true if the email's host is in the blocked set.
 * Returns false for partial input (e.g. "user@") so the UI doesn't
 * yell at the user mid-type.
 *
 * @param {string} email
 * @returns {boolean}
 */
export function isBlockedEmailProvider(email) {
  if (!email || typeof email !== 'string') return false;
  const cleaned = email.trim().toLowerCase();
  if (!cleaned.includes('@')) return false;
  const host = cleaned.split('@').pop().replace(/^\.+|\.+$/g, '');
  if (!host || !host.includes('.')) return false; // partial — wait for more
  return BLOCKED_EMAIL_HOSTS.has(host);
}
